(() => {
  const root = document.querySelector('.template-product');
  if (!root) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const ease = 'cubic-bezier(0.23, 1, 0.32, 1)';

  const formatIndex = (value) => String(value).padStart(2, '0');

  const enhancePressStates = (scope = root) => {
    scope.querySelectorAll('.product-form__submit, .product-form__input label, .op-pdp-editorial__cta, .op-pdp-tabs__tab, [data-op-accordion-trigger], .op-pdp-gallery-nav__button, [data-op-financing-open], [data-op-financing-close], [data-op-financing-plan], .op-pdp-financing-dialog__cart, .op-pdp-financing-dialog__credit-link').forEach((control) => {
      if (control.dataset.opPressReady === 'true') return;
      control.dataset.opPressReady = 'true';
      control.dataset.opPress = '';

      const release = () => control.classList.remove('is-pressed');
      control.addEventListener('pointerdown', () => control.classList.add('is-pressed'));
      control.addEventListener('pointerup', release);
      control.addEventListener('pointercancel', release);
      control.addEventListener('pointerleave', release);
    });
  };

  const enhanceReveals = (scope = root) => {
    const reveals = Array.from(scope.querySelectorAll('.op-pdp-reveal:not([data-op-reveal-ready])'));
    reveals.forEach((element) => { element.dataset.opRevealReady = 'true'; });

    if (reducedMotion || !('IntersectionObserver' in window)) {
      reveals.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries, revealObserver) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -5% 0px' });

    reveals.forEach((element) => {
      const rect = element.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) element.classList.add('is-visible');
      else observer.observe(element);
    });
  };

  const hasMeaningfulContent = (nodes) => nodes.some((node) => (
    node.nodeType === Node.ELEMENT_NODE || node.textContent.trim().length > 0
  ));

  const removeLegacyProductTabs = () => {
    root.querySelectorAll('.op-pdp-premium .halo-product-tab').forEach((tab) => tab.remove());
  };

  const enhanceDynamicAccordions = (scope = root) => {
    scope.querySelectorAll('[data-op-dynamic-accordions]:not([data-op-dynamic-ready])').forEach((section) => {
      const source = section.querySelector('[data-op-accordion-source]');
      const list = section.querySelector('[data-op-accordion-list]');
      if (!source || !list) return;

      section.dataset.opDynamicReady = 'true';
      const configuredDefaultTitle = section.dataset.defaultTitle || 'La pieza';
      const defaultTitle = /^(descripci[oó]n|description)$/i.test(configuredDefaultTitle.trim())
        ? 'La pieza'
        : configuredDefaultTitle;
      const groups = [];
      let current = {
        title: defaultTitle,
        nodes: []
      };

      Array.from(source.content.childNodes).forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE && node.matches('h3')) {
          if (hasMeaningfulContent(current.nodes)) groups.push(current);
          current = { title: node.textContent.trim() || 'Detalles', nodes: [] };
          return;
        }
        current.nodes.push(node.cloneNode(true));
      });
      if (hasMeaningfulContent(current.nodes)) groups.push(current);
      if (!groups.length) return;

      const normalizeTitle = (value) => value
        .toLocaleLowerCase('es')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();
      const legacyDescriptionTitles = new Set([
        'descripcion',
        'description',
        'detalles del producto',
        'product details'
      ]);
      const consolidatedGroups = [];
      groups.forEach((group) => {
        const title = legacyDescriptionTitles.has(normalizeTitle(group.title)) ? defaultTitle : group.title;
        const existing = consolidatedGroups.find((candidate) => normalizeTitle(candidate.title) === normalizeTitle(title));
        if (existing) {
          existing.nodes.push(...group.nodes);
          return;
        }
        consolidatedGroups.push({ title, nodes: group.nodes });
      });

      const namespace = (section.closest('.shopify-section')?.id || 'op-pdp-purchase').replace(/[^a-zA-Z0-9_-]/g, '');
      const openFirst = section.dataset.openFirst === 'true';

      consolidatedGroups.forEach((group, index) => {
        const questionId = `op-purchase-question-${namespace}-${index}`;
        const answerId = `op-purchase-answer-${namespace}-${index}`;
        const item = document.createElement('div');
        item.className = `op-pdp-purchase-accordion__item${index === 0 && openFirst ? ' is-open' : ''}`;
        item.dataset.opAccordionItem = '';

        const trigger = document.createElement('button');
        trigger.className = 'op-pdp-purchase-accordion__trigger';
        trigger.type = 'button';
        trigger.id = questionId;
        trigger.dataset.opAccordionTrigger = '';
        trigger.setAttribute('aria-controls', answerId);
        trigger.setAttribute('aria-expanded', index === 0 && openFirst ? 'true' : 'false');
        const triggerLabel = document.createElement('span');
        triggerLabel.textContent = group.title;
        const triggerIcon = document.createElement('span');
        triggerIcon.className = 'op-pdp-purchase-accordion__icon';
        triggerIcon.setAttribute('aria-hidden', 'true');
        triggerIcon.innerHTML = '<svg viewBox="0 0 16 16" fill="none"><path d="m4.5 6.25 3.5 3.5 3.5-3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        trigger.append(triggerLabel, triggerIcon);

        const panel = document.createElement('div');
        panel.className = 'op-pdp-purchase-accordion__panel';
        panel.id = answerId;
        panel.dataset.opAccordionPanel = '';
        panel.setAttribute('role', 'region');
        panel.setAttribute('aria-labelledby', questionId);
        panel.setAttribute('aria-hidden', index === 0 && openFirst ? 'false' : 'true');

        const panelInner = document.createElement('div');
        panelInner.className = 'op-pdp-purchase-accordion__panel-inner rte';
        group.nodes.forEach((node) => panelInner.appendChild(node));
        panel.appendChild(panelInner);
        item.append(trigger, panel);
        list.appendChild(item);
      });
    });
  };

  const enhanceAccordions = (scope = root) => {
    scope.querySelectorAll('[data-op-accordion]:not([data-op-accordion-ready])').forEach((accordion) => {
      accordion.dataset.opAccordionReady = 'true';
      const items = Array.from(accordion.querySelectorAll(':scope > [data-op-accordion-item], :scope > * > [data-op-accordion-item]'));
      const triggers = items.map((item) => item.querySelector('[data-op-accordion-trigger]')).filter(Boolean);

      const setOpen = (item, open) => {
        const trigger = item.querySelector('[data-op-accordion-trigger]');
        const panel = item.querySelector('[data-op-accordion-panel]');
        item.classList.toggle('is-open', open);
        trigger?.setAttribute('aria-expanded', open ? 'true' : 'false');
        panel?.setAttribute('aria-hidden', open ? 'false' : 'true');
        if (panel) panel.inert = !open;
      };

      items.forEach((item, index) => {
        const trigger = item.querySelector('[data-op-accordion-trigger]');
        if (!trigger) return;
        setOpen(item, item.classList.contains('is-open'));

        trigger.addEventListener('click', () => {
          const shouldOpen = !item.classList.contains('is-open');
          items.forEach((other) => setOpen(other, other === item && shouldOpen));
        });

        trigger.addEventListener('keydown', (event) => {
          let nextIndex = null;
          if (event.key === 'ArrowDown') nextIndex = (index + 1) % triggers.length;
          if (event.key === 'ArrowUp') nextIndex = (index - 1 + triggers.length) % triggers.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = triggers.length - 1;
          if (nextIndex === null) return;
          event.preventDefault();
          triggers[nextIndex]?.focus();
        });
      });
    });
  };

  const enhanceFinancing = (scope = root) => {
    scope.querySelectorAll('[data-op-financing]:not([data-op-financing-ready])').forEach((financing) => {
      financing.dataset.opFinancingReady = 'true';

      const productId = financing.dataset.productId;
      const variantsSource = financing.querySelector('[data-op-financing-variants]');
      const currency = financing.dataset.opCurrency || 'COP';
      let variants = [];
      let currentVariantId = null;

      try {
        variants = JSON.parse(variantsSource?.textContent || '[]');
      } catch (error) {
        variants = [];
      }

      const formatMoney = (amount) => {
        let formattedAmount = `$${Math.ceil(amount).toLocaleString('es-CO')}`;

        try {
          formattedAmount = new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency,
            maximumFractionDigits: 0
          }).format(Math.ceil(amount));
        } catch (error) {
          formattedAmount = `$${Math.ceil(amount).toLocaleString('es-CO')}`;
        }

        return formattedAmount;
      };

      const updateFinancingPrice = (variantId) => {
        if (!variantId) return;
        const variant = variants.find((item) => String(item.id) === String(variantId));
        if (!variant || !Number.isFinite(Number(variant.price))) return;
        currentVariantId = String(variant.id);
        const totalAmount = Number(variant.price) / 100;

        financing.querySelectorAll('[data-op-financing-total]').forEach((target) => {
          target.textContent = formatMoney(totalAmount);
        });

        financing.querySelectorAll('[data-op-financing-installment]').forEach((target) => {
          const installments = Math.max(1, Number(target.dataset.installments) || 6);
          target.textContent = `≈ ${formatMoney(totalAmount / installments)}`;
        });
      };

      const productInputs = Array.from(root.querySelectorAll(
        `#product-form-${productId} input[name="id"], #product-form-installment-${productId} input[name="id"]`
      ));
      productInputs.forEach((input) => {
        if (input.dataset.opFinancingBound === 'true') return;
        input.dataset.opFinancingBound = 'true';
        input.addEventListener('change', () => updateFinancingPrice(input.value));
      });
      updateFinancingPrice(productInputs.find((input) => input.value)?.value);

      const productForm = root.querySelector(`#product-form-${productId}`);
      const getProductSubmit = () => productForm?.querySelector('[data-btn-addToCart]');

      const setupPlanSelector = (dialog) => {
        if (!dialog) return;
        const planButtons = Array.from(dialog.querySelectorAll('[data-op-financing-plan]'));
        const summaryLabel = dialog.querySelector('[data-op-financing-selection-label]');
        const summaryPrice = dialog.querySelector('[data-op-financing-selection-price]');
        if (!planButtons.length || !summaryLabel || !summaryPrice) return;

        const selectPlan = (button, animate = true) => {
          const installments = Math.max(1, Number(button.dataset.installments) || 6);
          const isAlreadySelected = button.getAttribute('aria-pressed') === 'true';
          if (isAlreadySelected && animate) return;

          planButtons.forEach((planButton) => {
            const selected = planButton === button;
            planButton.classList.toggle('is-selected', selected);
            planButton.setAttribute('aria-pressed', selected ? 'true' : 'false');
          });

          summaryLabel.textContent = `Tu opción: ${installments} cuotas`;
          summaryPrice.dataset.installments = String(installments);
          updateFinancingPrice(currentVariantId || productInputs.find((input) => input.value)?.value);

          if (!animate || reducedMotion || typeof summaryPrice.animate !== 'function') return;
          summaryPrice.getAnimations().forEach((animation) => animation.cancel());
          summaryPrice.animate([
            { opacity: 0.35, transform: 'translate3d(0, 5px, 0)' },
            { opacity: 1, transform: 'translate3d(0, 0, 0)' }
          ], {
            duration: 220,
            easing,
            fill: 'both'
          });
        };

        planButtons.forEach((button) => {
          button.addEventListener('click', () => selectPlan(button));
        });
        selectPlan(planButtons.find((button) => button.classList.contains('is-selected')) || planButtons[planButtons.length - 1], false);
      };

      const setupDialog = (dialog, openButton, closeButton) => {
        if (!dialog || !openButton || !closeButton) return;
        let returnFocus = null;
        let closeTimer = null;
        const cartButton = dialog.querySelector('[data-op-financing-add-to-cart]');

        const syncCartState = () => {
          if (!cartButton) return;
          const productSubmit = getProductSubmit();
          const unavailable = !productForm || !productSubmit || productSubmit.disabled;
          cartButton.disabled = unavailable;
          cartButton.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
        };

        const openDialog = () => {
          window.clearTimeout(closeTimer);
          returnFocus = document.activeElement;
          syncCartState();
          document.documentElement.classList.add('op-financing-is-open');
          if (typeof dialog.showModal === 'function') {
            dialog.showModal();
          } else {
            dialog.setAttribute('open', '');
            dialog.setAttribute('aria-modal', 'true');
          }
          window.requestAnimationFrame(() => dialog.classList.add('is-visible'));
          closeButton.focus({ preventScroll: true });
        };

        const closeDialog = ({ restoreFocus = true, afterClose = null } = {}) => {
          dialog.classList.remove('is-visible');
          const finish = () => {
            if (typeof dialog.close === 'function' && dialog.open) dialog.close();
            else dialog.removeAttribute('open');
            document.documentElement.classList.remove('op-financing-is-open');
            if (restoreFocus) returnFocus?.focus?.({ preventScroll: true });
            if (typeof afterClose === 'function') afterClose();
          };
          if (reducedMotion) finish();
          else closeTimer = window.setTimeout(finish, 180);
        };

        cartButton?.addEventListener('click', () => {
          syncCartState();
          const productSubmit = getProductSubmit();
          if (cartButton.disabled || !productForm || !productSubmit) return;

          closeDialog({
            restoreFocus: false,
            afterClose: () => {
              if (typeof productForm.requestSubmit === 'function') {
                productForm.requestSubmit(productSubmit);
              } else {
                productSubmit.click();
              }
            }
          });
        });

        openButton.addEventListener('click', openDialog);
        closeButton.addEventListener('click', () => closeDialog());
        dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          closeDialog();
        });
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) closeDialog();
        });
      };

      financing.querySelectorAll('[data-op-financing-dialog]').forEach((dialog) => {
        const openButton = financing.querySelector(`[data-op-financing-open][aria-controls="${dialog.id}"]`);
        const closeButton = dialog.querySelector('[data-op-financing-close]');
        setupPlanSelector(dialog);
        setupDialog(dialog, openButton, closeButton);
      });
    });
  };

  const enhanceTabs = (scope = root) => {
    scope.querySelectorAll('[data-op-pdp-tabs]:not([data-op-tabs-ready])').forEach((section) => {
      const source = section.querySelector('[data-op-pdp-tabs-source]');
      const experience = section.querySelector('.op-pdp-tabs__experience');
      const list = section.querySelector('[data-op-pdp-tabs-list]');
      const panels = section.querySelector('[data-op-pdp-tabs-panels]');
      const indicator = section.querySelector('[data-op-pdp-tabs-indicator]');
      if (!source || !experience || !list || !panels || !indicator) return;

      section.dataset.opTabsReady = 'true';
      const groups = [];
      let current = {
        title: section.dataset.defaultTabTitle || 'La pieza',
        nodes: []
      };

      Array.from(source.content.childNodes).forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE && node.matches('h3')) {
          if (hasMeaningfulContent(current.nodes)) groups.push(current);
          current = { title: node.textContent.trim() || 'Detalles', nodes: [] };
          return;
        }
        current.nodes.push(node.cloneNode(true));
      });
      if (hasMeaningfulContent(current.nodes)) groups.push(current);
      if (!groups.length) return;

      const namespace = (section.closest('.shopify-section')?.id || 'op-pdp-details').replace(/[^a-zA-Z0-9_-]/g, '');

      groups.forEach((group, index) => {
        const tabId = `op-pdp-tab-${namespace}-${index}`;
        const panelId = `op-pdp-panel-${namespace}-${index}`;
        const tab = document.createElement('button');
        tab.className = 'op-pdp-tabs__tab';
        tab.type = 'button';
        tab.id = tabId;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', panelId);
        tab.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
        tab.tabIndex = index === 0 ? 0 : -1;
        tab.textContent = group.title;

        const panel = document.createElement('div');
        panel.className = 'op-pdp-tabs__panel rte';
        panel.id = panelId;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tabId);
        panel.tabIndex = 0;
        panel.hidden = index !== 0;
        group.nodes.forEach((node) => panel.appendChild(node));

        list.appendChild(tab);
        panels.appendChild(panel);
      });

      const tabs = Array.from(list.querySelectorAll('[role="tab"]'));
      const tabPanels = Array.from(panels.querySelectorAll('[role="tabpanel"]'));

      const moveIndicator = (tab) => {
        const listRect = list.getBoundingClientRect();
        const tabRect = tab.getBoundingClientRect();
        indicator.style.setProperty('--op-tab-x', `${tabRect.left - listRect.left}px`);
        indicator.style.setProperty('--op-tab-width', `${tabRect.width}px`);
      };

      const activate = (nextIndex, focus = false) => {
        tabs.forEach((tab, index) => {
          const active = index === nextIndex;
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.tabIndex = active ? 0 : -1;
          tab.classList.toggle('is-active', active);
          tabPanels[index].hidden = !active;
          tabPanels[index].classList.toggle('is-active', active);
        });

        const activeTab = tabs[nextIndex];
        activeTab.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
        moveIndicator(activeTab);
        if (focus) activeTab.focus({ preventScroll: true });
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(index));
        tab.addEventListener('keydown', (event) => {
          let nextIndex = null;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabs.length - 1;
          if (nextIndex === null) return;
          event.preventDefault();
          activate(nextIndex, true);
        });
      });

      list.addEventListener('scroll', () => {
        const selected = list.querySelector('[aria-selected="true"]');
        if (selected) moveIndicator(selected);
      }, { passive: true });

      experience.hidden = false;
      requestAnimationFrame(() => activate(0));

      if ('ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver(() => {
          const selected = list.querySelector('[aria-selected="true"]');
          if (selected) moveIndicator(selected);
        });
        resizeObserver.observe(list);
      }
      enhancePressStates(section);
    });
  };

  const enhanceGallery = () => {
    const gallery = root.querySelector('[data-image-gallery-main]');
    if (!gallery || gallery.dataset.opGalleryReady === 'true') return;

    const $ = window.jQuery;
    if (!$ || !$.fn?.slick || !gallery.classList.contains('slick-initialized')) {
      const attempts = Number(gallery.dataset.opGalleryAttempts || 0) + 1;
      gallery.dataset.opGalleryAttempts = String(attempts);
      if (attempts < 80) window.setTimeout(enhanceGallery, 120);
      return;
    }

    gallery.dataset.opGalleryReady = 'true';
    const $gallery = $(gallery);
    const total = Number(gallery.dataset.mediaCount) || gallery.querySelectorAll('.productView-image:not(.slick-cloned)').length || 1;
    const wrapper = gallery.closest('.productView-image-wrapper');
    const configElement = gallery.closest('.shopify-section')?.querySelector('[data-op-gallery-config]');
    let galleryConfig = { drag: true, speed: 250, thumbsDesktop: true, thumbsMobile: false, mobileNav: true };
    try {
      galleryConfig = { ...galleryConfig, ...JSON.parse(configElement?.textContent || '{}') };
    } catch (error) {
      // Keep the gallery usable if a theme editor setting is incomplete.
    }
    const allowDrag = galleryConfig.drag !== false;
    const configuredSpeed = Number.parseInt(galleryConfig.speed, 10);
    const slideSpeed = Number.isFinite(configuredSpeed) ? Math.min(600, Math.max(0, configuredSpeed)) : 250;
    let thumbnailRail = null;
    let thumbnailButtons = [];
    let layoutFrame = 0;

    const getSlideContainer = (index) => {
      const indexedSlide = gallery.querySelector(`.slick-slide[data-slick-index="${index}"]:not(.slick-cloned)`);
      const fallbackSlide = gallery.querySelectorAll('.productView-image:not(.slick-cloned)')[index];
      return (indexedSlide || fallbackSlide)?.querySelector('.productView-img-container') || null;
    };

    if (total > 1 && (galleryConfig.thumbsDesktop || galleryConfig.thumbsMobile) && wrapper) {
      thumbnailRail = document.createElement('div');
      thumbnailRail.className = `op-pdp-gallery-thumbs${galleryConfig.thumbsDesktop ? '' : ' op-pdp-gallery-thumbs--hide-desktop'}${galleryConfig.thumbsMobile ? '' : ' op-pdp-gallery-thumbs--hide-mobile'}`;
      thumbnailRail.dataset.opGalleryThumbs = '';
      thumbnailRail.setAttribute('role', 'group');
      thumbnailRail.setAttribute('aria-label', 'Miniaturas del producto');

      for (let index = 0; index < total; index += 1) {
        const container = getSlideContainer(index);
        const sourceImage = container?.querySelector('img:not(.zoomImg)');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `op-pdp-gallery-thumbs__button${index === 0 ? ' is-current' : ''}`;
        button.dataset.opGalleryThumb = String(index);
        button.setAttribute('aria-label', `Ver ${container?.querySelector('video, iframe') ? 'video' : 'imagen'} ${index + 1} de ${total}`);
        button.setAttribute('aria-pressed', index === 0 ? 'true' : 'false');

        if (sourceImage) {
          const image = document.createElement('img');
          const source = sourceImage.getAttribute('src') || sourceImage.dataset.src;
          if (source) {
            const url = new URL(source, document.baseURI);
            if (url.pathname.includes('/cdn/shop/')) url.searchParams.set('width', '160');
            image.src = url.toString();
          }
          image.alt = '';
          image.width = 64;
          image.height = 64;
          image.loading = 'lazy';
          button.appendChild(image);
        } else {
          button.textContent = '▶';
        }

        thumbnailRail.appendChild(button);
      }

      wrapper.insertAdjacentElement('afterend', thumbnailRail);
      thumbnailButtons = Array.from(thumbnailRail.querySelectorAll('[data-op-gallery-thumb]'));
    }

    const setGalleryHeight = (index, immediate = false) => {
      const list = gallery.querySelector('.slick-list');
      const container = getSlideContainer(index);
      if (!list || !container) return;

      const image = container.querySelector('img:not(.zoomImg)');
      const declaredRatio = Number.parseFloat(getComputedStyle(container).getPropertyValue('--op-media-aspect'));
      const naturalRatio = image?.naturalWidth && image?.naturalHeight
        ? image.naturalWidth / image.naturalHeight
        : 0;
      const ratio = declaredRatio > 0 ? declaredRatio : (naturalRatio > 0 ? naturalRatio : 1);
      const width = list.clientWidth || gallery.clientWidth || container.clientWidth;
      if (!width || !Number.isFinite(ratio)) return;

      const nextHeight = Math.ceil(width / ratio);
      if (immediate || reducedMotion) list.style.transition = 'none';
      list.style.setProperty('--op-gallery-height', `${nextHeight}px`);
      if (immediate || reducedMotion) {
        window.requestAnimationFrame(() => {
          list.style.transition = '';
        });
      }
    };

    const hydrateSlide = (index) => {
      const container = getSlideContainer(index);
      container?.querySelectorAll('img:not(.zoomImg)').forEach((image) => {
        if (!image.getAttribute('src') && image.dataset.src) image.src = image.dataset.src;
        if (!image.getAttribute('srcset') && image.dataset.srcset) image.srcset = image.dataset.srcset;
      });
    };

    const hydrateWindow = (index) => {
      if (total <= 0) return;
      [index, (index + 1) % total, (index - 1 + total) % total].forEach(hydrateSlide);
    };

    const refreshGalleryLayout = (reposition = false) => {
      window.cancelAnimationFrame(layoutFrame);
      layoutFrame = window.requestAnimationFrame(() => {
        layoutFrame = window.requestAnimationFrame(() => {
          if (!gallery.isConnected || !gallery.classList.contains('slick-initialized')) return;
          try {
            if (reposition) $gallery.slick('setPosition');
            setGalleryHeight($gallery.slick('slickCurrentSlide') || 0, true);
          } catch (error) {
            // The native gallery remains available if Slick is rebuilding.
          }
        });
      });
    };

    try {
      $gallery.slick('slickSetOption', {
        adaptiveHeight: false,
        speed: reducedMotion ? 0 : slideSpeed,
        cssEase: ease,
        swipe: allowDrag,
        swipeToSlide: false,
        draggable: allowDrag,
        touchMove: allowDrag,
        touchThreshold: 8,
        verticalSwiping: false,
        edgeFriction: 0.18,
        waitForAnimate: false,
        accessibility: true
      }, false);
    } catch (error) {
      // The gallery remains usable with the theme's native configuration.
    }

    hydrateWindow($gallery.slick('slickCurrentSlide') || 0);
    gallery.querySelectorAll('img').forEach((image) => {
      if (image.complete) {
        if (typeof image.decode === 'function') {
          image.decode().catch(() => {}).finally(() => {
            refreshGalleryLayout(false);
          });
        } else {
          refreshGalleryLayout(false);
        }
        return;
      }
      image.addEventListener('load', () => {
        refreshGalleryLayout(false);
      }, { once: true });
      image.addEventListener('error', () => refreshGalleryLayout(false), { once: true });
    });

    window.addEventListener('load', () => refreshGalleryLayout(true), { once: true });
    window.addEventListener('pageshow', () => refreshGalleryLayout(true));

    if ('ResizeObserver' in window && wrapper) {
      let previousWidth = 0;
      const resizeObserver = new ResizeObserver(([entry]) => {
        const width = Math.round(entry.contentRect.width);
        if (width === previousWidth) return;
        previousWidth = width;
        refreshGalleryLayout(true);
      });
      resizeObserver.observe(wrapper);
    }

    const status = document.createElement('div');
    status.className = `op-pdp-gallery-nav${galleryConfig.mobileNav ? '' : ' op-pdp-gallery-nav--hide-mobile'}`;
    status.setAttribute('role', 'group');
    status.setAttribute('aria-label', 'Navegación de imágenes del producto');
    status.innerHTML = `
      <button class="op-pdp-gallery-nav__button" type="button" aria-label="Imagen anterior" data-op-gallery-prev>
        <svg aria-hidden="true" viewBox="0 0 16 16" fill="none"><path d="M10.5 3.5 6 8l4.5 4.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
      <span class="op-pdp-gallery-nav__segments" aria-hidden="true" data-op-gallery-segments></span>
      <button class="op-pdp-gallery-nav__button" type="button" aria-label="Imagen siguiente" data-op-gallery-next>
        <svg aria-hidden="true" viewBox="0 0 16 16" fill="none"><path d="m5.5 3.5 4.5 4.5-4.5 4.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
    `;
    wrapper?.querySelectorAll('.op-pdp-gallery-status, .op-pdp-gallery-nav').forEach((legacyStatus) => legacyStatus.remove());
    wrapper?.appendChild(status);

    const segmentsRoot = status.querySelector('[data-op-gallery-segments]');
    const segments = Array.from({ length: total }, (_value, index) => {
      const segment = document.createElement('span');
      segment.className = 'op-pdp-gallery-nav__segment';
      segment.dataset.opGallerySegment = String(index);
      segmentsRoot.appendChild(segment);
      return segment;
    });
    const previousButton = status.querySelector('[data-op-gallery-prev]');
    const nextButton = status.querySelector('[data-op-gallery-next]');
    const goBy = (delta) => {
      const current = $gallery.slick('slickCurrentSlide') || 0;
      const next = (current + delta + total) % total;
      hydrateWindow(next);
      setGalleryHeight(next);
      $gallery.slick('slickGoTo', next);
    };
    previousButton?.addEventListener('click', () => goBy(-1));
    nextButton?.addEventListener('click', () => goBy(1));
    thumbnailButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const index = Number(button.dataset.opGalleryThumb);
        if (!Number.isInteger(index) || index < 0 || index >= total) return;
        hydrateWindow(index);
        setGalleryHeight(index);
        $gallery.slick('slickGoTo', index);
      });
    });

    const syncMedia = (index) => {
      gallery.querySelectorAll('video').forEach((video) => {
        const slide = video.closest('.slick-slide');
        const active = slide?.classList.contains('slick-current');
        if (!active) {
          video.pause();
          return;
        }
        video.muted = true;
        video.playsInline = true;
        video.play().catch(() => {});
      });
      segments.forEach((segment, segmentIndex) => segment.classList.toggle('is-active', segmentIndex === index));
      thumbnailButtons.forEach((button, buttonIndex) => {
        const isCurrent = buttonIndex === index;
        button.classList.toggle('is-current', isCurrent);
        button.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
        if (isCurrent && thumbnailRail?.clientWidth) {
          const railRect = thumbnailRail.getBoundingClientRect();
          const buttonRect = button.getBoundingClientRect();
          if (buttonRect.right > railRect.right) {
            thumbnailRail.scrollBy({ left: buttonRect.right - railRect.right + 8, behavior: reducedMotion ? 'auto' : 'smooth' });
          } else if (buttonRect.left < railRect.left) {
            thumbnailRail.scrollBy({ left: buttonRect.left - railRect.left - 8, behavior: reducedMotion ? 'auto' : 'smooth' });
          }
        }
      });
    };

    $gallery.off('.opPdp')
      .on('beforeChange.opPdp', (_event, _slick, _currentSlide, nextSlide) => {
        hydrateWindow(nextSlide || 0);
        setGalleryHeight(nextSlide || 0);
      })
      .on('afterChange.opPdp reInit.opPdp', (_event, _slick, currentSlide) => {
        syncMedia(currentSlide || 0);
        setGalleryHeight(currentSlide || 0);
      });
    syncMedia($gallery.slick('slickCurrentSlide') || 0);
    setGalleryHeight($gallery.slick('slickCurrentSlide') || 0, true);
    refreshGalleryLayout();
    window.setTimeout(refreshGalleryLayout, 80);
    window.setTimeout(refreshGalleryLayout, 320);
    enhancePressStates(status);
  };

  const enhanceReviews = (scope = root) => {
    if (window.Shopify?.designMode) return;
    scope.querySelectorAll('[data-op-review-section]:not([data-op-review-ready])').forEach((section) => {
      section.dataset.opReviewReady = 'true';
      const widget = section.querySelector('.jdgm-rev-widg');
      const host = section.closest('.shopify-section') || section;
      if (!widget) {
        host.hidden = true;
        return;
      }

      const syncVisibility = () => {
        const hasReviews = widget.querySelector('.jdgm-rev') !== null;
        const shownByApp = getComputedStyle(widget).display !== 'none' && getComputedStyle(widget).visibility !== 'hidden';
        host.hidden = !(hasReviews && shownByApp);
        if (!host.hidden) section.querySelector('.op-pdp-reveal')?.classList.add('is-visible');
      };

      const observer = new MutationObserver(syncVisibility);
      observer.observe(widget, { attributes: true, childList: true, subtree: true });
      window.setTimeout(syncVisibility, 1200);
      window.addEventListener('pageshow', syncVisibility);
    });
  };

  const enhanceRecentlyViewed = (scope = root) => {
    if (window.Shopify?.designMode) return;
    scope.querySelectorAll('[data-recently-viewed-block]:not([data-op-recent-ready])').forEach((block) => {
      block.dataset.opRecentReady = 'true';
      const host = block.closest('.shopify-section') || block;
      let pendingCheck = 0;

      const syncProducts = () => {
        const hasProduct = block.querySelector('.recentlyViewed-row a[href*="/products/"]') !== null;
        if (hasProduct) {
          host.hidden = false;
        } else if (block.classList.contains('ajax-loaded')) {
          host.hidden = true;
        }
      };

      const observer = new MutationObserver(() => {
        if (block.querySelector('.recentlyViewed-row a[href*="/products/"]')) {
          window.clearTimeout(pendingCheck);
          syncProducts();
        } else if (block.classList.contains('ajax-loaded') && !pendingCheck) {
          pendingCheck = window.setTimeout(() => {
            pendingCheck = 0;
            syncProducts();
          }, 2500);
        }
      });
      observer.observe(block, { attributes: true, attributeFilter: ['class'], childList: true, subtree: true });
      if (block.querySelector('.recentlyViewed-row a[href*="/products/"]')) {
        syncProducts();
      } else if (block.classList.contains('ajax-loaded')) {
        pendingCheck = window.setTimeout(() => {
          pendingCheck = 0;
          syncProducts();
        }, 2500);
      }
    });
  };

  const init = (scope = root) => {
    removeLegacyProductTabs();
    enhanceDynamicAccordions(scope);
    enhanceAccordions(scope);
    enhanceTabs(scope);
    enhanceFinancing(scope);
    enhancePressStates(scope);
    enhanceReveals(scope);
    enhanceGallery();
    enhanceReviews(scope);
    enhanceRecentlyViewed(scope);
  };

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
